export interface SearcherField {
  label: string;
  value: string;
  glyph: string;
}

export interface SearcherDefinition {
  id: string;
  label: string;
  iconUrl: string;
  urlOnly: boolean;
  url: string;
  fields: SearcherField[];
}

const ICON_BASE = '/assets/img/searchers';

export const SEARCHER_CATALOG: Record<string, SearcherDefinition> = {
  paquetes: {
    id: 'paquetes',
    label: 'Paquetes',
    iconUrl: `${ICON_BASE}/paquetes.png`,
    urlOnly: false,
    url: '',
    fields: [
      { label: 'Origen', value: 'Buenos Aires', glyph: '⌖' },
      { label: 'Destino', value: 'Cusco', glyph: '⌖' },
      { label: 'Fecha', value: 'Julio 2026', glyph: '▦' },
      { label: 'Pasajeros', value: '1 pasajero', glyph: '♙' },
    ],
  },
  vuelos: {
    id: 'vuelos',
    label: 'Vuelos',
    iconUrl: `${ICON_BASE}/vuelos.png`,
    urlOnly: false,
    url: '',
    fields: [
      { label: 'Origen', value: 'Ciudad o aeropuerto', glyph: '⌖' },
      { label: 'Destino', value: 'Ciudad o aeropuerto', glyph: '⌖' },
      { label: 'Fecha', value: 'Ida → Vuelta', glyph: '▦' },
      { label: 'Pasajeros', value: '1 pasajero', glyph: '♙' },
    ],
  },
  hoteles: {
    id: 'hoteles',
    label: 'Hoteles',
    iconUrl: `${ICON_BASE}/hoteles.png`,
    urlOnly: false,
    url: '',
    fields: [
      { label: 'Destino', value: 'Ciudad o zona', glyph: '⌖' },
      { label: 'Entrada', value: 'Seleccioná fecha', glyph: '▦' },
      { label: 'Salida', value: 'Seleccioná fecha', glyph: '▦' },
      { label: 'Huéspedes', value: '2 huéspedes', glyph: '♙' },
    ],
  },
  actividades: {
    id: 'actividades',
    label: 'Actividades',
    iconUrl: `${ICON_BASE}/actividades.png`,
    urlOnly: true,
    url: '',
    fields: [
      { label: 'Destino', value: 'Ciudad o destino', glyph: '⌖' },
      { label: 'Fecha', value: 'Seleccioná fecha', glyph: '▦' },
      { label: 'Personas', value: '2 personas', glyph: '♙' },
      { label: 'Categoría', value: 'Todas', glyph: '⌄' },
    ],
  },
  traslados: {
    id: 'traslados',
    label: 'Traslados',
    iconUrl: `${ICON_BASE}/traslados.png`,
    urlOnly: true,
    url: '',
    fields: [
      { label: 'Desde', value: 'Aeropuerto u hotel', glyph: '⌖' },
      { label: 'Hasta', value: 'Hotel o destino', glyph: '⌖' },
      { label: 'Fecha', value: 'Seleccioná fecha', glyph: '▦' },
      { label: 'Pasajeros', value: '2 pasajeros', glyph: '♙' },
    ],
  },
  autos: {
    id: 'autos',
    label: 'Autos',
    iconUrl: `${ICON_BASE}/autos.png`,
    urlOnly: true,
    url: '',
    fields: [
      { label: 'Ciudad', value: 'Lugar de retiro', glyph: '⌖' },
      { label: 'Retiro', value: 'Fecha y hora', glyph: '▦' },
      { label: 'Devolución', value: 'Fecha y hora', glyph: '▦' },
      { label: 'Conductor', value: '30–65 años', glyph: '♙' },
    ],
  },
  // External hotels action (acciones.hoteles). Distinct from `hoteles`, the BM form.
  'hoteles-url': {
    id: 'hoteles-url',
    label: 'Hoteles',
    iconUrl: `${ICON_BASE}/hoteles.png`,
    urlOnly: true,
    url: '',
    fields: [],
  },
};

export const SEARCHER_IDS = Object.keys(SEARCHER_CATALOG);
export const DEFAULT_SEARCHER_TAB_IDS = [
  'paquetes',
  'vuelos',
  'hoteles',
  'actividades',
  'traslados',
  'autos',
] as const;

export interface SearcherItem {
  id: string;
  label?: string;
  iconUrl?: string;
  urlOnly?: boolean;
  url?: string;
  show?: boolean;
}

export function resolveSearcher(item: SearcherItem): SearcherDefinition & { show: boolean } {
  const base = SEARCHER_CATALOG[item.id];
  const fallback: SearcherDefinition = {
    id: item.id,
    label: item.label ?? item.id,
    iconUrl: item.iconUrl ?? '',
    urlOnly: item.urlOnly ?? false,
    url: item.url ?? '',
    fields: [],
  };
  const definition = base ?? fallback;
  return {
    ...definition,
    label: item.label ?? definition.label,
    iconUrl: item.iconUrl ?? definition.iconUrl,
    urlOnly: item.urlOnly ?? definition.urlOnly,
    url: item.url ?? definition.url,
    show: item.show !== false,
  };
}

export function defaultSearcherItems(): SearcherItem[] {
  return DEFAULT_SEARCHER_TAB_IDS.map((id) => ({ id, show: true }));
}

export function visibleSearchers(
  items: SearcherItem[],
): (SearcherDefinition & { show: boolean })[] {
  return items.map(resolveSearcher).filter((searcher) => searcher.show);
}

export function resolveActiveSearcherId(items: SearcherItem[], activeId?: string): string {
  const selectable = visibleSearchers(items).filter((searcher) => !searcher.urlOnly);
  const match = selectable.find((searcher) => searcher.id === activeId);
  return match?.id ?? selectable[0]?.id ?? '';
}
